/**
 * @fileoverview Song Search Panel Component
 *
 * Right-side panel for searching and browsing preset songs.
 * Integrates Fuse.js for fuzzy search with multiple search modes.
 *
 * **Features:**
 * - Fuzzy search with Fuse.js
 * - Three search modes: Spanish+English, Spanish-only, Metadata
 * - Search highlighting in results
 * - Virtualized list for performance
 * - Empty states and loading states
 *
 * @module components/SongSearchPanel
 */

import { useState, useMemo } from 'react'
import Fuse, { type IFuseOptions } from 'fuse.js'
import type { Doc } from '../../convex/_generated/dataModel'
import { SearchModeToggle } from './SearchModeToggle'
import type { SearchMode } from './SearchModeToggle'
import { SongListItem } from './SongListItem'
import { Search, X, Music, Volume2, VolumeX } from 'lucide-react'

type AudioFilter = 'all' | 'with-audio' | 'without-audio'

interface SongSearchPanelProps {
  songs: Array<Doc<'songs'> & { audioGeneration?: Doc<'temporGenerations'> | null }>
  selectedSongId?: string
  onSelectSong: (songId: string) => void
  isLoading?: boolean
}

/**
 * Get Fuse.js search keys based on search mode
 */
function getFuseKeys(mode: SearchMode): string[] {
  switch (mode) {
    case 'both':
      return ['title', 'sections.spanish', 'sections.english', 'targetVerb', 'sourceVerb']
    case 'spanish':
      return ['sections.spanish', 'targetVerb']
    case 'metadata':
      return ['title', 'targetVerb', 'sourceVerb', 'tags']
    default:
      return ['title']
  }
}

export function SongSearchPanel({
  songs,
  selectedSongId,
  onSelectSong,
  isLoading = false,
}: SongSearchPanelProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [searchMode, setSearchMode] = useState<SearchMode>('both')
  const [audioFilter, setAudioFilter] = useState<AudioFilter>('with-audio')
  const [isSearchFocused, setIsSearchFocused] = useState(false)

  // Initialize Fuse.js with current search mode
  const fuse = useMemo(() => {
    // Guard against empty or undefined songs array
    if (!songs || songs.length === 0) {
      return new Fuse([], { keys: ['title'] })
    }

    const options: IFuseOptions<Doc<'songs'>> = {
      keys: getFuseKeys(searchMode),
      threshold: 0.3, // 0 = exact match, 1 = match anything
      includeScore: true,
      includeMatches: true,
      minMatchCharLength: 2,
      // Ignore undefined fields to prevent errors with optional properties
      ignoreFieldNorm: true,
      // Handle null/undefined values gracefully
      getFn: (obj, path) => {
        const value = Fuse.config.getFn(obj, path)
        // Return empty array for undefined/null to prevent .add() errors
        return value ?? []
      },
    }
    return new Fuse(songs, options)
  }, [songs, searchMode])

  // Apply audio filter
  const audioFilteredSongs = useMemo(() => {
    if (audioFilter === 'all') {
      return songs
    }
    return songs.filter((song) => {
      // Check BOTH audioGeneration AND audioStorageId (preset songs use audioStorageId)
      const hasAudio = !!song.audioGeneration || !!song.audioStorageId
      return audioFilter === 'with-audio' ? hasAudio : !hasAudio
    })
  }, [songs, audioFilter])

  // Perform fuzzy search on audio-filtered songs
  const filteredSongs = useMemo(() => {
    if (!searchTerm.trim()) {
      return audioFilteredSongs
    }
    const results = fuse.search(searchTerm)
    // Filter by audio after search
    return results.map((result) => result.item).filter((song) => {
      if (audioFilter === 'all') return true
      // Check BOTH audioGeneration AND audioStorageId (preset songs use audioStorageId)
      const hasAudio = !!song.audioGeneration || !!song.audioStorageId
      return audioFilter === 'with-audio' ? hasAudio : !hasAudio
    })
  }, [fuse, searchTerm, audioFilteredSongs, audioFilter])

  return (
    <div className="flex flex-col h-full bg-black">
      {/* Header */}
      <div className="flex-shrink-0 px-4 py-4 bg-black border-b border-gray-700">
        <h2 className="text-lg font-semibold text-white mb-4">Song Library</h2>

        {/* Search Input */}
        <div className="relative mb-4">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => setIsSearchFocused(true)}
            onBlur={() => setIsSearchFocused(false)}
            placeholder="Search songs..."
            className="w-full pl-4 pr-10 py-2 bg-black border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent placeholder-gray-600 font-bold"
            aria-label="Search songs"
          />
          {searchTerm ? (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          ) : (
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          )}
        </div>

        {/* Search Mode Toggle - Only show when input is focused */}
        {isSearchFocused && (
          <SearchModeToggle mode={searchMode} onChange={setSearchMode} className="mb-4" />
        )}

        {/* Audio Filter Toggle */}
        <div className="mt-3">
          <div className="text-xs font-medium text-white mb-2">Audio Filter</div>
          <div className="flex gap-2">
            <button
              onClick={() => setAudioFilter('with-audio')}
              className={`
                flex-1 px-3 py-2 sm:py-2.5 text-sm sm:text-lg font-bold rounded-lg border-2 transition-colors flex items-center justify-center gap-1
                ${
                  audioFilter === 'with-audio'
                    ? 'bg-cyan-600 text-white border-cyan-600'
                    : 'bg-black text-white border-gray-600 hover:border-cyan-400'
                }
              `}
              aria-pressed={audioFilter === 'with-audio'}
            >
              <Volume2 className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">With Audio</span>
              <span className="sm:hidden">Audio</span>
            </button>
            <button
              onClick={() => setAudioFilter('all')}
              className={`
                flex-1 px-3 py-2 sm:py-2.5 text-sm sm:text-lg font-bold rounded-lg border-2 transition-colors
                ${
                  audioFilter === 'all'
                    ? 'bg-cyan-600 text-white border-cyan-600'
                    : 'bg-black text-white border-gray-600 hover:border-cyan-400'
                }
              `}
              aria-pressed={audioFilter === 'all'}
            >
              All
            </button>
            <button
              onClick={() => setAudioFilter('without-audio')}
              className={`
                flex-1 px-3 py-2 sm:py-2.5 text-sm sm:text-lg font-bold rounded-lg border-2 transition-colors flex items-center justify-center gap-1
                ${
                  audioFilter === 'without-audio'
                    ? 'bg-cyan-600 text-white border-cyan-600'
                    : 'bg-black text-white border-gray-600 hover:border-cyan-400'
                }
              `}
              aria-pressed={audioFilter === 'without-audio'}
            >
              <VolumeX className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">No Audio</span>
              <span className="sm:hidden">None</span>
            </button>
          </div>
        </div>
      </div>

      {/* Results Count */}
      <div className="flex-shrink-0 px-4 py-2 bg-black border-b border-gray-700">
        <p className="text-sm text-gray-400">
          {isLoading ? (
            <span className="inline-flex items-center gap-2">
              <span className="animate-spin inline-block w-4 h-4 border-2 border-cyan-600 border-t-transparent rounded-full" />
              Loading songs...
            </span>
          ) : (
            <>
              {filteredSongs.length} {filteredSongs.length === 1 ? 'song' : 'songs'}
              {searchTerm && ` matching "${searchTerm}"`}
            </>
          )}
        </p>
      </div>

      {/* Song List */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {isLoading ? (
          // Loading skeleton
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="animate-pulse bg-gray-900 rounded-lg border border-gray-700 p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-gray-700 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-700 rounded w-3/4" />
                    <div className="h-3 bg-gray-800 rounded w-1/2" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredSongs.length === 0 ? (
          // Empty state
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Music className="w-16 h-16 text-gray-600 mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">
              {searchTerm ? 'No songs found' : 'No songs available'}
            </h3>
            <p className="text-sm text-gray-400 max-w-sm">
              {searchTerm
                ? `Try adjusting your search term or changing the search mode.`
                : 'Preset songs will appear here once they are loaded.'}
            </p>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="mt-4 px-4 py-2 sm:py-2.5 text-sm sm:text-lg font-bold bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors"
              >
                Clear search
              </button>
            )}
          </div>
        ) : (
          // Song list
          <div className="space-y-2">
            {filteredSongs.map((song) => (
              <SongListItem
                key={song._id}
                song={song}
                isSelected={selectedSongId === song._id}
                onClick={() => onSelectSong(song._id)}
                searchHighlight={searchTerm}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
